import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import appendQuery from 'append-query';
import mapboxgl from 'mapbox-gl';
import { isEmpty } from 'lodash-es';
import vaDebounce from 'platform/utilities/data/debounce';
import recordEvent from 'platform/monitoring/record-event';
import { mapboxToken } from '../utils/mapboxToken';
import { getServiceDisplayName } from '../reducers/searchQuery';

// Components
import Alert from '../components/Alert';
import CommunityCareWarningBanner from '../components/CommunityCareWarningBanner';
import ControlResultsHolder from '../components/ControlResultsHolder';
import ControlsAndMapContainer from '../components/ControlsAndMapContainer';
import EmergencyCareAlert from '../components/EmergencyCareAlert';
import MobileMapSearchResult from '../components/MobileMapSearchResult';
import NoResultsMessage from '../components/NoResultsMessage';
import PaginationWrapper from '../components/PaginationWrapper';
import PpmsServiceError from '../components/PpmsServiceError';
import RenderMap from '../components/RenderMap';
import ResultsList from '../components/ResultsList';
import SearchForm from '../components/search-form';
import SearchResultsHeader from '../components/SearchResultsHeader';
import SegmentedControl from '../components/SegmentedControl';

// Actions
import {
  clearSearchResults,
  searchWithBounds,
  updateSearchQuery,
} from '../actions/search';
import { fetchVAFacility } from '../actions/locations';
import {
  genBBoxFromAddress,
  genSearchAreaFromCenter,
  mapMoved,
  selectMobileMapPin,
} from '../actions/mapbox';

// Utils
import {
  facilitiesPpmsSuppressAll,
  facilityLocatorMobileMapUpdate,
  facilityLocatorAutosuggestVAMCServices,
  facilityLocatorPredictiveLocationSearch,
  showFacilityLocatorNoticeAboutNonVACare,
} from '../utils/featureFlagSelectors';
import { FacilitiesMapTypes } from '../types';
import {
  setFocus,
  buildMarker,
  resetMapElements,
  orderMapTabStops,
  suppressMapAttributionTabStops,
} from '../utils/helpers';
import {
  EMERGENCY_CARE_SERVICES,
  LocationType,
  MapboxInit,
  MAX_SEARCH_AREA,
} from '../constants';
import { distBetween } from '../utils/facilityDistance';
import { recordZoomEvent, recordPanEvent } from '../utils/analytics';
import { stripSerializedArrayKeys } from '../utils/urlQuery';

const DEFAULT_MAP_TOP_OFFSET = 160;
const STICKY_PADDING = 12;
const MIN_MAP_HEIGHT = 500;

const mapboxGlContainer = 'mapbox-gl-container';
const zoomMessageDivID = 'screenreader-zoom-message';

// Populates the #map-instructions sr-only live region. Wired to the Mapbox
// canvas focus event so announcements fire when the user focuses the element
// that actually accepts arrow-key input, not the outer wrapper div.
// Exported for unit testing — the canvas wiring in setupMap() depends on
// Mapbox GL and is covered by manual verification.
export const speakMapInstructions = () => {
  const el = document.getElementById('map-instructions');
  if (el) {
    el.innerText =
      'Use the arrow keys to move the searchable area of the interactive map.';
  }
};

// Wires accessibility onto the real interactive element — the Mapbox canvas,
// not the wrapper div. role="application" is what makes JAWS/Narrator pass
// arrow keys through to the map instead of intercepting them, and the
// aria-label restores the accessible name the wrapper div used to provide.
// Guarded with a dataset flag so we never stack duplicate focus handlers on
// the same canvas — Mapbox can fire 'load' more than once on a single map
// instance (e.g. after a style change), and setupMap() runs on resize/tab
// changes too. Exported for unit testing.
export const wireCanvasA11y = canvas => {
  if (!canvas || canvas.getAttribute('data-fl-a11y-wired') === 'true') return;
  canvas.setAttribute('role', 'application');
  canvas.setAttribute('aria-label', 'Find VA locations on an interactive map');
  canvas.setAttribute('aria-describedby', 'map-instructions');
  canvas.addEventListener('focus', speakMapInstructions);
  canvas.setAttribute('data-fl-a11y-wired', 'true');
};

const FacilitiesMap = props => {
  const {
    mobileMapPinSelected,
    mobileMapUpdateEnabled,
    vamcAutoSuggestEnabled,
  } = props;
  const [map, setMap] = useState(null);
  const [verticalSize, setVerticalSize] = useState(0);
  const [horizontalSize, setHorizontalSize] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 481);
  const [isSmallDesktop, setIsSmallDesktop] = useState(
    window.innerWidth >= 1024,
  );
  const [mapboxTokenValid, setMapboxTokenValid] = useState(true);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth > 481 && window.innerWidth <= 1023,
  );
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [isMapSticky, setIsMapSticky] = useState(false);
  const [isMapBottomAnchored, setIsMapBottomAnchored] = useState(false);
  const [mapTopOffset, setMapTopOffset] = useState(DEFAULT_MAP_TOP_OFFSET);
  const [mapStickyLeft, setMapStickyLeft] = useState(0);
  const [mapStickyWidth, setMapStickyWidth] = useState(0);

  // refs
  const mapboxContainerRef = useRef(null);
  const controlsAndMapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapboxGlContainerRef = useRef(null);
  const searchResultTitleRef = useRef(null);
  const searchResultMessageRef = useRef();
  const lastZoomRef = useRef(3);
  const hasInitialPositionRef = useRef(false);
  // Synchronous mirror of the `map` state, so setupMap() can read the
  // previous Mapbox instance and tear it down before creating a new one.
  // Reading from React state would lag (state updates are async), causing
  // stale Mapbox canvases / NavigationControls to accumulate inside the
  // wrapper on every resize / tab switch and producing duplicate keyboard
  // tab stops on the canvas.
  const mapInstanceRef = useRef(null);
  // For the mobile list/map tabs: when the user leaves the map for the list we
  // remember the map's camera and which search it framed. Returning to the map
  // without having run a new search restores that camera (and skips the
  // auto-fit), so their panning/zooming survives the tab switch instead of
  // snapping back to the results bounds.
  const savedMobileCameraRef = useRef(null);
  const savedMobileCameraSearchIdRef = useRef(null);
  // Set by setupMap when it applied a saved camera; consumed by the marker
  // render so it skips the one auto-fit that would override that camera.
  const cameraRestoredRef = useRef(false);

  // Capture the map's natural position before scroll handler attaches (for sticky calculations)
  useLayoutEffect(() => {
    const captureInitialPosition = () => {
      const el = mapContainerRef.current;
      if (el && isSmallDesktop && !isMapSticky && !isMapBottomAnchored) {
        const rect = el.getBoundingClientRect();
        setMapStickyLeft(rect.left);
        setMapStickyWidth(rect.width);
        hasInitialPositionRef.current = true;
      }
    };
    captureInitialPosition();
    window.addEventListener('resize', captureInitialPosition);
    return () => window.removeEventListener('resize', captureInitialPosition);
  }, [isSmallDesktop, isMapBottomAnchored, isMapSticky]);

  /**
   * Search when the component renders with a sharable url
   */
  const searchWithUrl = () => {
    // Check for scenario when results are in the store
    if (!!props.location.search && props.results && props.results.length > 0) {
      return;
    }
    const { location } = props;

    if (!isEmpty(location.query)) {
      const vamcServiceDisplay =
        location.query.facilityType === LocationType.HEALTH
          ? getServiceDisplayName(
              location.query.serviceType,
              props.vaHealthServicesData,
            )
          : null;

      props.updateSearchQuery({
        facilityType: location.query.facilityType,
        serviceType: location.query.serviceType,
        searchString: location.query.address,
        vamcServiceDisplay,
      });
    }

    if (location.query.address && location.query.facilityType) {
      const needsServiceType =
        location.query.facilityType === LocationType.CC_PROVIDER;
      const hasRequiredServiceType =
        !needsServiceType || location.query.serviceType;

      if (hasRequiredServiceType) {
        const expandedRadius =
          location.query.facilityType === LocationType.BENEFITS &&
          !location.query.serviceType;

        props.genBBoxFromAddress(
          {
            searchString: location.query.address,
            context: location.query.context,
            facilityType: location.query.facilityType,
            serviceType: location.query.serviceType,
          },
          { expandedRadius },
        );
        setIsSearching(true);
      }
    }
  };

  const updateUrlParams = params => {
    const { location, currentQuery } = props;
    const queryParams = {
      // Carrying `bounds[]` forward from the parsed query would re-serialize it
      // as `bounds[][]`, one more pair every search. `bounds` below is the only
      // source for it (CMS #20560).
      ...stripSerializedArrayKeys(location.query),
      page: currentQuery.currentPage,
      address: currentQuery.searchString,
      facilityType: currentQuery.facilityType,
      serviceType: currentQuery.serviceType,
      latitude: props.currentQuery.position?.latitude,
      longitude: props.currentQuery.position?.longitude,
      radius: props.currentQuery.radius && props.currentQuery.radius.toFixed(),
      bounds: props.currentQuery.bounds,
      ...params,
    };

    const queryStringObj = appendQuery(
      `/find-locations${location.pathname}`,
      queryParams,
    );

    browserHistory.push(queryStringObj);
  };

  const addMapMarker = searchCoords => {
    const markerElement = buildMarker(
      'currentPos',
      null,
      props.selectMobileMapPin,
      mobileMapUpdateEnabled,
    );
    new mapboxgl.Marker(markerElement)
      .setLngLat([searchCoords.lng, searchCoords.lat])
      .addTo(map);
  };

  const renderMarkers = useCallback(
    locations => {
      if (locations.length === 0) return;

      const locationBounds = new mapboxgl.LngLatBounds();

      locations.forEach((loc, index) => {
        const attrs = {
          letter: index + 1,
        };
        locationBounds.extend(
          new mapboxgl.LngLat(loc.attributes.long, loc.attributes.lat),
        );
        const markerElement = buildMarker(
          'location',
          { loc, attrs },
          props.selectMobileMapPin,
          mobileMapUpdateEnabled,
        );
        new mapboxgl.Marker(markerElement)
          .setLngLat([loc.attributes.long, loc.attributes.lat])
          .addTo(map);
      });

      if (props.currentQuery.searchCoords) {
        const { searchCoords } = props.currentQuery;
        addMapMarker(searchCoords);
        locationBounds.extend(
          new mapboxgl.LngLat(searchCoords.lng, searchCoords.lat),
        );
        // Skip the auto-fit when setupMap restored the user's saved camera
        // (returning to the mobile map tab without a new search), so we keep
        // their pan/zoom instead of snapping back to the results bounds.
        if (!cameraRestoredRef.current) {
          map.fitBounds(locationBounds, { padding: 20 });
        }
      }

      if (props.currentQuery.searchArea) {
        const { locationCoords } = props.currentQuery.searchArea;
        addMapMarker(locationCoords);
        locationBounds.extend(
          new mapboxgl.LngLat(locationCoords.lng, locationCoords.lat),
        );
      }

      // Markers were just (re)added to the end of the canvas container;
      // reassert tab order so the zoom controls stay ahead of the markers.
      orderMapTabStops(document.getElementById(mapboxGlContainer));
    },
    // addMapMarker is intentionally omitted since it's defined inline without
    // useCallback - including it would cause unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, props.currentQuery, props.selectMobileMapPin, mobileMapUpdateEnabled],
  );

  const handleSearch = (formValues = null) => {
    resetMapElements();
    const queryToUse = formValues
      ? { ...props.currentQuery, ...formValues }
      : props.currentQuery;
    const { facilityType, serviceType, searchString } = queryToUse;
    const expandedRadius =
      facilityType === LocationType.BENEFITS && !serviceType;

    updateUrlParams({
      address: searchString,
      facilityType,
      serviceType,
    });

    const canvas = map?.getCanvas();
    const mapAspectRatio =
      canvas && canvas.height > 0 ? canvas.width / canvas.height : 1;

    // geocodeFeature is a submit-only hint (the suggestion the user picked, or
    // the first one the autosuggest offered). Keep it out of the query we hand
    // down, since processFeaturesBBox echoes that query straight into the
    // persisted searchQuery and from there into the URL (CMS #20560).
    const { geocodeFeature, ...queryForSearch } = queryToUse;

    props.genBBoxFromAddress(queryForSearch, {
      expandedRadius,
      mapAspectRatio,
      feature: geocodeFeature,
    });

    setIsSearching(true);
  };

  const calculateSearchArea = () => {
    if (map) {
      const currentBounds = map.getBounds();
      const { _ne, _sw } = currentBounds;
      return distBetween(_ne.lat, _ne.lng, _sw.lat, _sw.lng);
    }

    return 0;
  };

  const handleSearchArea = () => {
    if (!map) return;

    resetMapElements();

    const { currentQuery } = props;
    const center = map.getCenter().wrap();
    const bounds = map.getBounds();

    // Record event happens on load. The "search this area" path has no
    // service-type autosuggest interaction, so the VA-health svc-type facets are
    // all 0 here. (Other `fl-search` events, like map pan distance, may emit a
    // different shape.)
    recordEvent({
      event: 'fl-search',
      'fl-search-fac-type': currentQuery.facilityType,
      'fl-search-svc-type': currentQuery.serviceType,
      'fl-search-svc-type-no-selection-search': 0,
      'fl-search-svc-type-no-results-input': 0,
      'fl-search-svc-type-no-results-search-all': 0,
      'fl-search-svc-type-selection-search': 0,
      'fl-search-svc-type-via-keyboard': 0,
    });

    const currentMapBoundsDistance = calculateSearchArea();

    setSearchInitiated(true);
    props.genSearchAreaFromCenter({
      lat: center.lat,
      lng: center.lng,
      currentMapBoundsDistance,
      currentBounds: [
        bounds._sw.lng,
        bounds._sw.lat,
        bounds._ne.lng,
        bounds._ne.lat,
      ],
    });
  };

  const handlePageSelect = e => {
    const { page } = e.detail;

    resetMapElements();
    const { currentQuery } = props;
    const coords = currentQuery.position;
    const { radius } = currentQuery;
    const center = [coords.latitude, coords.longitude];
    props.searchWithBounds({
      bounds: currentQuery.bounds,
      facilityType: currentQuery.facilityType,
      serviceType: currentQuery.serviceType,
      page,
      center,
      radius,
      // Already resolved for this center by the search that produced these
      // bounds, so PPMS types need no reverse geocode (CMS #20560).
      address: currentQuery.mapBoxQuery?.placeName,
    });
  };

  const speakZoom = (searchRadius, zoomDirection) => {
    const screenreaderZoomElement = document.getElementById(zoomMessageDivID);

    if (screenreaderZoomElement) {
      // delay to allow time for the search area button text to be read
      setTimeout(() => {
        screenreaderZoomElement.innerText = `zooming ${zoomDirection}, ${Math.round(
          searchRadius,
        )} miles`;
      }, 750);
    }
  };

  const setMapEventHandlers = () => {
    map.on('dragend', () => {
      props.mapMoved(calculateSearchArea());
      recordPanEvent(map.getCenter(), props.currentQuery);
    });

    map.on('zoomend', e => {
      const currentZoom = parseInt(map.getZoom(), 10);

      // Programmatic zooms (e.g. search settling) have no originalEvent —
      // keep the ref in sync with the settled zoom but don't record.
      if (!e.originalEvent) {
        lastZoomRef.current = currentZoom;
        return;
      }

      const searchRadius = calculateSearchArea();

      props.mapMoved(searchRadius);

      // speakZoom and recordZoomEvent both rely on a known previous zoom level
      // to compute direction; on the first manual zoom we just stamp the ref
      // and skip announcing/recording so we don't coerce null → 0.
      if (lastZoomRef.current == null) {
        lastZoomRef.current = currentZoom;
        return;
      }

      recordZoomEvent(lastZoomRef.current, currentZoom);

      if (lastZoomRef.current !== currentZoom) {
        const zoomDirection = currentZoom > lastZoomRef.current ? 'in' : 'out';
        speakZoom(searchRadius, zoomDirection);
        lastZoomRef.current = currentZoom;
      }
    });
  };

  const setupMap = () => {
    const mapContainerElement = document.getElementById(mapboxGlContainer);
    if (!mapContainerElement) {
      return null;
    }

    if (
      mapInstanceRef.current &&
      typeof mapInstanceRef.current.remove === 'function'
    ) {
      try {
        mapInstanceRef.current.remove();
      } catch (e) {
        // Mapbox can throw during remove() if the GL context is already lost
        // (happens occasionally on rapid resize). Swallow so we still proceed
        // to the DOM sweep + new instance below.
      }
      mapInstanceRef.current = null;
    }

    // Defense-in-depth: even after map.remove(), a previously-added
    // NavigationControl whose lifecycle is not perfectly tracked by Mapbox 1.x
    // can leave its container behind. Sweep any leftover canvas-container or
    // control-container nodes from the wrapper before initializing the new map.
    mapContainerElement
      .querySelectorAll(
        '.mapboxgl-canvas-container, .mapboxgl-control-container',
      )
      .forEach(node => node.remove());

    // Restore the camera the user left the map at, but only if they haven't run
    // a new search since (matching search id). Otherwise fall through to the
    // default view so a fresh search frames its own results.
    let restoreCamera = null;
    if (
      isMobile &&
      savedMobileCameraRef.current &&
      savedMobileCameraSearchIdRef.current === props.currentQuery.id
    ) {
      restoreCamera = savedMobileCameraRef.current;
    }
    savedMobileCameraRef.current = null;
    cameraRestoredRef.current = !!restoreCamera;

    mapboxgl.accessToken = mapboxToken;
    const mapInit = new mapboxgl.Map({
      container: mapboxGlContainer,
      style: 'mapbox://styles/mapbox/outdoors-v11',
      center: restoreCamera
        ? restoreCamera.center
        : [MapboxInit.centerInit.lng, MapboxInit.centerInit.lat],
      zoom: restoreCamera ? restoreCamera.zoom : MapboxInit.zoomInit,
    });

    mapInstanceRef.current = mapInit;

    mapInit.addControl(
      new mapboxgl.NavigationControl({
        // Hide rotation control.
        showCompass: false,
      }),
      'top-left',
    );

    mapInit.on('load', () => {
      mapInit.resize();

      // Wire a11y (role/aria/focus announcement) onto the real interactive
      // element — the canvas — so tabbing lands directly on the element that
      // accepts arrow keys.
      wireCanvasA11y(mapInit.getCanvas());

      // Slot the zoom controls between the canvas and the result markers so
      // tab order reads: canvas (arrow-key pan) -> zoom in/out -> markers.
      orderMapTabStops(mapContainerElement);

      // Drop the Mapbox logo + attribution links out of the tab order so they
      // don't become a confusing run of stops between the controls and the
      // canvas (they stay visible/readable, just not tabbable).
      suppressMapAttributionTabStops(mapContainerElement);
    });

    return mapInit;
  };

  /**
   * Setup map on resize
   */
  const setMapResize = () => {
    setTimeout(function mapSetup() {
      setMap(setupMap());
    }, 10);
  };

  /**
   * Setup map on tab change
   */
  const segmentOnChange = tab => {
    // Leaving the map for the list: remember the current camera (and the search
    // it framed) so returning to the map can restore the user's view instead of
    // recreating the map at the default nationwide position. See setupMap.
    if (tab === 0 && isMobile && mapInstanceRef.current) {
      try {
        savedMobileCameraRef.current = {
          center: mapInstanceRef.current.getCenter(),
          zoom: mapInstanceRef.current.getZoom(),
        };
        savedMobileCameraSearchIdRef.current = props.currentQuery.id;
      } catch (e) {
        savedMobileCameraRef.current = null;
      }
    }
    setSelectedTab(tab);
    props.selectMobileMapPin(null);
    if (tab === 1) {
      // Switching to the map tab: (re)create the map now that its container is
      // rendered. We only do this on the map tab — recreating on the list tab
      // built a throwaway map and raced with this one on rapid toggles.
      setMapResize();
    } else {
      // Leaving the map tab: drop the map state (its container is unmounted).
      // The instance is torn down and rebuilt when the map tab reopens.
      setMap(null);
    }
  };

  const shouldRenderSearchArea = () => {
    return props.currentQuery?.mapMoved;
  };

  const searchAreaButtonEnabled = () =>
    calculateSearchArea() < MAX_SEARCH_AREA &&
    props.currentQuery.facilityType &&
    (props.currentQuery.facilityType === LocationType.CC_PROVIDER
      ? !!props.currentQuery.serviceType
      : true);

  const mapboxTokenHealth = async () => {
    const response = await fetch(
      `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8,mapbox.mapbox-terrain-v2.json?secure&access_token=${mapboxToken}`,
      { method: 'HEAD' },
    );

    return response.ok;
  };

  useEffect(() => {
    (async () => {
      let tokenHealth = false;
      try {
        tokenHealth = await mapboxTokenHealth();
      } catch {
        // do nothing - prevent from escalating error
      }
      if (!tokenHealth) {
        setMapboxTokenValid(false);
      }
    })();
  }, []);

  const renderView = () => {
    if (!mapboxTokenValid) {
      return (
        <div className="vads-u-margin-x--2 medium-screen:vads-u-margin-x--2 vads-u-margin-bottom--4">
          <p>
            <strong>Facility Locator is down for maintenance</strong>
          </p>
          <p>
            We’re making some updates to Facility Locator. We’re sorry it’s not
            working right now. Please check back soon.
          </p>
        </div>
      );
    }

    // This block renders the desktop and mobile view. It ensures that the desktop map
    // gets re-loaded when resizing from mobile to desktop.
    const { currentQuery, results, pagination, searchError } = props;

    const currentPage = pagination ? pagination.currentPage : 1;
    const totalPages = pagination ? pagination.totalPages : 1;
    const { facilityType, serviceType } = currentQuery;
    const queryContext = currentQuery.context;
    const isEmergencyCareType = facilityType === LocationType.EMERGENCY_CARE;
    const isCcpEmergencyCareTypes =
      EMERGENCY_CARE_SERVICES.includes(serviceType);

    const paginationWrapper = () => {
      return (
        <PaginationWrapper
          handlePageSelect={handlePageSelect}
          currentPage={currentPage}
          totalPages={totalPages}
          results={results}
          inProgress={currentQuery.inProgress}
        />
      );
    };

    const resultsList = () => {
      return (
        <ResultsList
          isMobile={isMobile}
          query={currentQuery}
          searchResultMessageRef={searchResultMessageRef}
          updateUrlParams={updateUrlParams}
        />
      );
    };

    if (
      map &&
      !isMobile &&
      !window.document.getElementById(mapboxGlContainer)?.children.length
    ) {
      setMapResize();
    }

    const renderStickyMap = () => {
      let mapContainerStyle = {};
      if (isMapSticky) {
        mapContainerStyle = {
          top: `${mapTopOffset}px`,
          left: `${mapStickyLeft}px`,
          width: `${mapStickyWidth}px`,
        };
      } else if (isMapBottomAnchored) {
        mapContainerStyle = {
          width: `${mapStickyWidth}px`,
        };
      }
      return (
        <div
          className={`map-and-message-container${
            isMapSticky ? ' map-and-message-container--sticky' : ''
          }${
            isMapBottomAnchored
              ? ' map-and-message-container--bottom-anchored'
              : ''
          }`}
          ref={mapContainerRef}
          style={mapContainerStyle}
        >
          <RenderMap
            currentQuery={currentQuery}
            handleSearchArea={handleSearchArea}
            isSearching={isSearching}
            mapboxGlContainer={mapboxGlContainer}
            map={map}
            mobile={false}
            mobileMapUpdateEnabled={mobileMapUpdateEnabled}
            results={results}
            searchAreaButtonEnabled={!!map && searchAreaButtonEnabled()}
            selectMobileMapPin={props.selectMobileMapPin}
            shouldRenderSearchArea={!!map && shouldRenderSearchArea()}
            smallDesktop
            zoomMessageDivID={zoomMessageDivID}
            ref={mapboxGlContainerRef}
          />
        </div>
      );
    };

    return (
      <div
        id="facilities-map-container"
        className={!isMobile ? 'tablet-or-greater-container' : undefined}
        ref={mapboxContainerRef}
      >
        {props.suppressPPMS && (
          <Alert
            displayType="warning"
            title="Some search options aren’t working right now"
            description="We’re sorry. Searches for non-VA facilities such as community providers and urgent care are currently unavailable. We’re working to fix this. Please check back soon."
          />
        )}
        <ControlsAndMapContainer
          isSmallDesktop={isSmallDesktop}
          ref={controlsAndMapRef}
        >
          <ControlResultsHolder isSmallDesktop={isSmallDesktop}>
            <PpmsServiceError currentQuery={props.currentQuery} />
            <SearchForm
              currentQuery={currentQuery}
              isMobile={isMobile}
              isSmallDesktop={isSmallDesktop}
              isTablet={isTablet}
              location={props.location}
              mobileMapUpdateEnabled={mobileMapUpdateEnabled}
              onChange={props.updateSearchQuery}
              onSubmit={handleSearch}
              searchInitiated={searchInitiated}
              selectMobileMapPin={props.selectMobileMapPin}
              setSearchInitiated={setSearchInitiated}
              suppressPPMS={props.suppressPPMS}
              vaHealthServicesData={props.vaHealthServicesData}
              vamcAutoSuggestEnabled={vamcAutoSuggestEnabled}
            />
            <EmergencyCareAlert
              shouldShow={isEmergencyCareType || isCcpEmergencyCareTypes}
            />
            <div id="search-results-title" ref={searchResultTitleRef}>
              {!searchError && (
                <SearchResultsHeader
                  results={results}
                  facilityType={facilityType}
                  serviceType={serviceType}
                  context={queryContext}
                  specialtyMap={props.specialties}
                  inProgress={currentQuery.inProgress}
                  pagination={pagination}
                  radius={currentQuery.radius}
                />
              )}
              {searchError && <p />}
            </div>
            {!isMobile && (
              <>
                {isSmallDesktop && (
                  <div
                    className="search-results-container vads-u-padding-x--0p5 vads-u-padding-top--0p5 columns"
                    id="searchResultsContainer"
                  >
                    <div className="facility-search-results">
                      {resultsList()}
                    </div>
                  </div>
                )}
                {isTablet && (
                  <>
                    <div
                      className={
                        !isMobile ? 'tablet-results-map-container' : undefined
                      }
                    >
                      <div
                        className="columns search-results-container vads-u-padding-right--1p5 vads-u-padding-left--0p25"
                        id="searchResultsContainer"
                      >
                        <div className="facility-search-results">
                          {resultsList()}
                        </div>
                      </div>
                      <RenderMap
                        currentQuery={currentQuery}
                        handleSearchArea={handleSearchArea}
                        isSearching={isSearching}
                        mapboxGlContainer={mapboxGlContainer}
                        map={map}
                        mobile={false}
                        mobileMapUpdateEnabled={mobileMapUpdateEnabled}
                        results={results}
                        selectMobileMapPin={props.selectMobileMapPin}
                        searchAreaButtonEnabled={
                          !!map && searchAreaButtonEnabled()
                        }
                        shouldRenderSearchArea={
                          !!map && shouldRenderSearchArea()
                        }
                        smallDesktop={false}
                        zoomMessageDivID={zoomMessageDivID}
                        ref={mapboxGlContainerRef}
                      />
                    </div>
                    {paginationWrapper()}
                  </>
                )}
              </>
            )}
          </ControlResultsHolder>
          {(isMapSticky || isMapBottomAnchored) && (
            <div
              aria-hidden
              style={{
                flexGrow: 1,
                minHeight: MIN_MAP_HEIGHT,
                height: 'calc(100vh - 120px)',
              }}
            />
          )}
          {isSmallDesktop && renderStickyMap()}
        </ControlsAndMapContainer>
        {isSmallDesktop && paginationWrapper()}

        {isMobile && (
          <div className="columns small-12">
            {mobileMapUpdateEnabled ? (
              <>
                <SegmentedControl
                  label="Facility results view"
                  labels={['View List', 'View Map']}
                  onChange={segmentOnChange}
                  selected={selectedTab}
                />
                {selectedTab === 0 ? (
                  <>
                    <div className="facility-search-results">
                      {resultsList()}
                    </div>
                    {paginationWrapper()}{' '}
                  </>
                ) : (
                  <>
                    <RenderMap
                      currentQuery={currentQuery}
                      handleSearchArea={handleSearchArea}
                      isSearching={isSearching}
                      mapboxGlContainer={mapboxGlContainer}
                      map={map}
                      mobile
                      mobileMapUpdateEnabled={mobileMapUpdateEnabled}
                      results={results}
                      searchAreaButtonEnabled={
                        !!map && searchAreaButtonEnabled()
                      }
                      selectMobileMapPin={props.selectMobileMapPin}
                      shouldRenderSearchArea={!!map && shouldRenderSearchArea()}
                      smallDesktop={false}
                      zoomMessageDivID={zoomMessageDivID}
                      ref={mapboxGlContainerRef}
                    />
                    {currentQuery.searchStarted && !results.length && (
                      <NoResultsMessage
                        resultRef={searchResultMessageRef}
                        resultsFound={false}
                        searchStarted
                      />
                    )}
                    <MobileMapSearchResult
                      mobileMapPinSelected={mobileMapPinSelected}
                      query={currentQuery}
                      searchResultMessageRef={searchResultMessageRef}
                    />
                  </>
                )}
              </>
            ) : (
              <Tabs>
                <TabList>
                  <Tab className="small-6 tab">View List</Tab>
                  <Tab onClick={setMapResize} className="small-6 tab">
                    View Map
                  </Tab>
                </TabList>
                <TabPanel>
                  <div className="facility-search-results">{resultsList()}</div>
                  {paginationWrapper()}
                </TabPanel>
                <TabPanel>
                  <RenderMap
                    currentQuery={currentQuery}
                    handleSearchArea={handleSearchArea}
                    isSearching={isSearching}
                    mapboxGlContainer={mapboxGlContainer}
                    map={map}
                    mobile
                    mobileMapUpdateEnabled
                    results={results}
                    searchAreaButtonEnabled={!!map && searchAreaButtonEnabled()}
                    selectMobileMapPin={props.selectMobileMapPin}
                    shouldRenderSearchArea={!!map && shouldRenderSearchArea()}
                    smallDesktop={false}
                    zoomMessageDivID={zoomMessageDivID}
                    ref={mapboxGlContainerRef}
                  />
                  {currentQuery.searchStarted && !results.length && (
                    <NoResultsMessage
                      resultRef={searchResultMessageRef}
                      resultsFound={false}
                      searchStarted
                    />
                  )}
                </TabPanel>
              </Tabs>
            )}
          </div>
        )}
      </div>
    );
  };

  const searchCurrentArea = () => {
    const { currentQuery } = props;
    const { searchArea, context, searchString } = currentQuery;
    const coords = currentQuery.position;
    const { radius } = currentQuery;
    const center = [coords.latitude, coords.longitude];
    if (searchArea) {
      updateUrlParams({
        context,
        searchString,
      });
      props.searchWithBounds({
        bounds: props.currentQuery.bounds,
        facilityType: props.currentQuery.facilityType,
        serviceType: props.currentQuery.serviceType,
        page: props.currentQuery.currentPage,
        center,
        radius,
        // genSearchAreaFromCenter just reverse-geocoded this center and stored
        // the result here, so PPMS types must not look it up again (CMS #20560).
        address: props.currentQuery.mapBoxQuery?.placeName,
      });
    }
  };

  const setUpResizeEventListener = () => {
    const setBreakpoints = () => {
      const screenIsMobile = window.innerWidth <= 481;
      const screenIsSmallDesktop = window.innerWidth >= 1024;
      const screenIsTablet =
        window.innerWidth > 481 && window.innerWidth <= 1023;

      setIsMobile(screenIsMobile);
      setIsSmallDesktop(screenIsSmallDesktop);
      setIsTablet(screenIsTablet);
    };
    const debouncedResize = vaDebounce(250, setBreakpoints);
    searchWithUrl();

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  };

  const handleSearchOnQueryChange = () => {
    if (isSearching) {
      updateUrlParams({
        context: props.currentQuery.context,
        address: props.currentQuery.searchString,
      });
      const { currentQuery } = props;
      const coords = currentQuery.position;
      const { radius } = currentQuery;
      const center = [coords.latitude, coords.longitude];
      const resultsPage = currentQuery.currentPage;

      if (!props.searchBoundsInProgress) {
        props.searchWithBounds({
          bounds: props.currentQuery.bounds,
          facilityType: props.currentQuery.facilityType,
          serviceType: props.currentQuery.serviceType,
          page: resultsPage,
          center,
          radius,
          // processFeaturesBBox stored the place name for this exact center on
          // the query, so PPMS types need no reverse geocode (CMS #20560).
          address: props.currentQuery.mapBoxQuery?.placeName,
        });
        setIsSearching(false);
      }
    }
  };

  const handleMapOnNoResultsFound = () => {
    if (
      props.results &&
      props.results.length === 0 &&
      map &&
      props.currentQuery.searchCoords
    ) {
      const { searchCoords } = props.currentQuery;
      const locationBounds = new mapboxgl.LngLatBounds();
      addMapMarker(searchCoords);
      locationBounds.extend(
        new mapboxgl.LngLat(searchCoords.lng, searchCoords.lat),
      );
      map.fitBounds(locationBounds, { maxZoom: 12 });
    }
  };

  useEffect(
    () => {
      if (map) {
        setMapEventHandlers();
      }
    },
    // We intentionally omit setMapEventHandlers from deps - we only want to
    // set up event handlers when map is created or searchCoords changes,
    // not on every render when the function reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, props.currentQuery.searchCoords],
  );

  useEffect(
    () => {
      searchCurrentArea();
    },
    // searchCurrentArea is intentionally omitted - we only want this to run
    // when searchArea changes, not when the function reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.currentQuery.searchArea],
  );

  useEffect(
    () => {
      if (mapboxContainerRef.current && !map) {
        setMap(setupMap());
        setUpResizeEventListener();
      }
    },
    // We intentionally omit setupMap and setUpResizeEventListener from deps -
    // this effect should only run once when the container ref is available
    // and the map hasn't been initialized yet
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapboxContainerRef, map],
  );

  // Tear down the final Mapbox instance on unmount so the canvas / controls
  // don't leak when the user navigates away without a full page reload (SPA
  // navigation). Kept in a separate effect with an empty dep array so the
  // cleanup ONLY fires at unmount — pairing it with the mount effect above
  // would tear down the map every time the `map` state changes.
  useEffect(() => {
    return () => {
      if (
        mapInstanceRef.current &&
        typeof mapInstanceRef.current.remove === 'function'
      ) {
        try {
          mapInstanceRef.current.remove();
        } catch (e) {
          // see setupMap() teardown rationale
        }
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Re-process URL params when vaHealthServicesData becomes available
  // This handles the case where the page loads with URL params but the
  // drupal static data hasn't loaded yet
  useEffect(
    () => {
      const { location } = props;
      if (
        props.vaHealthServicesData?.data &&
        !isEmpty(location.query) &&
        location.query.facilityType === LocationType.HEALTH &&
        location.query.serviceType &&
        !props.currentQuery.vamcServiceDisplay
      ) {
        const vamcServiceDisplay = getServiceDisplayName(
          location.query.serviceType,
          props.vaHealthServicesData,
        );
        if (vamcServiceDisplay) {
          props.updateSearchQuery({
            vamcServiceDisplay,
          });
        }
      }
    },
    // Only re-run when vaHealthServicesData loads; location.query and
    // currentQuery.vamcServiceDisplay are intentionally omitted because other
    // effects handle those changes. updateSearchQuery is a stable dispatch ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.vaHealthServicesData],
  );

  useEffect(
    () => {
      handleSearchOnQueryChange();
    },
    // handleSearchOnQueryChange reads props/state but is not memoized;
    // currentQuery.id is the reducer's unique search token — only changes
    // when a new search is initiated, which is the sole intended trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.currentQuery.id],
  );

  useEffect(
    () => {
      if (!map) return;
      renderMarkers(props.results);
      // The restored-camera grace applies to just this first render after the
      // map was recreated; clear it so the next genuine search re-fits.
      cameraRestoredRef.current = false;
    },
    // renderMarkers is intentionally omitted - it's a useCallback that depends
    // on map and props, and we only want to re-render markers when results change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.results, map],
  );

  useEffect(
    () => {
      const { inProgress, searchStarted } = props.currentQuery;

      if (searchResultTitleRef.current && !inProgress && searchStarted) {
        setFocus(searchResultTitleRef.current);
      }
    },
    // Ref values (searchResultTitleRef) shouldn't be in deps - they don't
    // trigger re-renders. We trigger on inProgress/searchStarted state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.currentQuery.inProgress, props.currentQuery.searchStarted],
  );

  useEffect(
    () => {
      handleMapOnNoResultsFound();
    },
    // handleMapOnNoResultsFound is intentionally omitted - we only want this
    // to run when searchCoords or results change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.currentQuery.searchCoords, props.results],
  );

  useEffect(() => {
    if (searchResultMessageRef.current) {
      setFocus(searchResultMessageRef.current);
    }
  }, [props.results, props.currentQuery.inProgress, props.searchError]);

  // Return the map to its neutral pre-search view when a search is cleared
  // (e.g. an invalid submit), so it doesn't stay zoomed into the previous
  // result area. Triggers on searchStarted going true → false, which covers
  // both the sync and async invalid paths (CMS #22751).
  const prevSearchStartedRef = useRef(props.currentQuery.searchStarted);
  useEffect(() => {
    const wasStarted = prevSearchStartedRef.current;
    prevSearchStartedRef.current = props.currentQuery.searchStarted;

    if (wasStarted && !props.currentQuery.searchStarted && map) {
      resetMapElements();
      map.flyTo({
        center: [MapboxInit.centerInit.lng, MapboxInit.centerInit.lat],
        zoom: MapboxInit.zoomInit,
      });
    }
  }, [props.currentQuery.searchStarted, map]);

  // When a submitted search resolves to an invalid/too-broad location, drop the
  // stale search params from the URL so a reload doesn't replay the old search
  // or leave stale bounds behind (CMS #22751).
  useEffect(
    () => {
      if (
        props.currentQuery.invalidLocation &&
        props.location?.query?.address
      ) {
        // Strip first: once the key is spelled `bounds[]` the delete below no
        // longer matches it, and the stale bounds survive (CMS #22751).
        const restQuery = stripSerializedArrayKeys(props.location.query);
        delete restQuery.address;
        delete restQuery.bounds;
        delete restQuery.latitude;
        delete restQuery.longitude;
        delete restQuery.radius;
        browserHistory.push(
          appendQuery(`/find-locations${props.location.pathname}`, restQuery),
        );
      }
    },
    // Only react to the invalid flag flipping; location/query changes here are
    // a consequence of this effect, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.currentQuery.invalidLocation],
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { height, width } = entry.contentRect;
        setTimeout(() => {
          // to prevent the map from being resized multiple times
          setVerticalSize(height);
          setHorizontalSize(width);
        }, 200);
      }
    });

    if (mapboxContainerRef.current) {
      resizeObserver.observe(mapboxContainerRef.current);
    } else {
      resizeObserver.disconnect();
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [mapboxContainerRef]);

  useEffect(() => {
    if (map && !isMobile) {
      map.resize();
    }
  }, [map, horizontalSize, verticalSize, isMobile]);

  // Handle sticky map behavior for desktop
  useEffect(() => {
    if (!isSmallDesktop) {
      setIsMapSticky(false);
      return undefined;
    }

    // Perf: Only run scroll logic once per frame, and only update state if values change
    let ticking = false;
    let rafId = null;

    /**
     * Handles sticky map positioning and offset calculation for desktop view.
     *
     * Logic:
     * - Uses #header-default for the main VA.gov site header (not just any <header>).
     * - Sums the heights of all visible .usa-alert banners (multiple can exist, e.g. sitewide and page alerts).
     * - Maintains a base STICKY_PADDING offset for visual separation.
     * - Ensures the sticky map sits below both the header and any visible alerts.
     * - Handles dynamic layout changes (resizes, alert visibility, etc) robustly.
     *
     * Why:
     * - This ensures the sticky map never overlaps the header or alerts, even if banners are added/removed dynamically.
     * - Multiple alerts can stack, so we sum their heights for correct offset.
     * - Uses explicit selectors (#header-default, .usa-alert) for maintainability and to avoid accidental breakage if the DOM structure changes.
     *
     * If you update the header or alert markup, update selectors here as needed.
     */
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      rafId = requestAnimationFrame(() => {
        const container = controlsAndMapRef.current;
        // Use the specific header for VA.gov layout
        const header = document.getElementById('header-default');

        // Sum the heights of all visible .usa-alert banners (can be >1)
        const alertBanners = Array.from(
          document.querySelectorAll('.usa-alert'),
        );
        const totalAlertHeight = alertBanners.reduce((sum, alert) => {
          const rect = alert.getBoundingClientRect();
          const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
          return inViewport && rect.height > 0 ? sum + rect.height : sum;
        }, 0);

        if (container) {
          const containerRect = container.getBoundingClientRect();

          // Start with base offset
          let topOffset = STICKY_PADDING;

          // Add header height if present and visible
          if (header) {
            const headerRect = header.getBoundingClientRect();
            if (headerRect.bottom > 0) {
              topOffset = Math.max(
                topOffset,
                headerRect.bottom + STICKY_PADDING,
              );
            }
          }

          // Add all visible alert banner heights
          if (totalAlertHeight > 0) {
            // Place sticky map below the last alert
            // (header already included above, so just add alert height)
            topOffset = Math.max(
              topOffset,
              (header ? header.getBoundingClientRect().bottom : 0) +
                totalAlertHeight +
                STICKY_PADDING,
            );
          }

          setMapTopOffset(prev => (prev !== topOffset ? topOffset : prev));

          // Get the map container element to capture its exact position
          const mapElement = mapContainerRef.current;

          // Get the actual map height from the element (set via CSS)
          const mapHeight = mapElement
            ? mapElement.offsetHeight
            : MIN_MAP_HEIGHT;

          // Sticky behavior:
          // 1. Become sticky when container top scrolls past the header/alerts
          // 2. Anchor to bottom when map bottom would go past container bottom
          // 3. Return to sticky when scrolling back up
          const pastTopThreshold = containerRect.top <= topOffset;
          const mapBottomPosition = topOffset + mapHeight;
          const containerBottomInView =
            containerRect.bottom > mapBottomPosition;

          const shouldBeSticky = pastTopThreshold && containerBottomInView;
          const shouldBeBottomAnchored =
            pastTopThreshold && !containerBottomInView;

          // Capture the map's natural position BEFORE it becomes sticky
          // Only update if not sticky or not bottom-anchored, or on initial load
          // (Initial capture now handled in useLayoutEffect; see above)
          if (
            mapElement &&
            (!hasInitialPositionRef.current ||
              (!shouldBeSticky && !shouldBeBottomAnchored))
          ) {
            const mapRect = mapElement.getBoundingClientRect();
            setMapStickyLeft(prev =>
              prev !== mapRect.left ? mapRect.left : prev,
            );
            setMapStickyWidth(prev =>
              prev !== mapRect.width ? mapRect.width : prev,
            );
            hasInitialPositionRef.current = true;
          }

          setIsMapSticky(prev =>
            prev !== shouldBeSticky ? shouldBeSticky : prev,
          );
          setIsMapBottomAnchored(prev =>
            prev !== shouldBeBottomAnchored ? shouldBeBottomAnchored : prev,
          );
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isSmallDesktop]);

  return (
    <>
      <h1 className="vads-u-margin-x--2 medium-screen:vads-u-margin-x--2">
        Find VA locations
      </h1>
      {mapboxTokenValid && (
        <p className="vads-u-margin-x--2 medium-screen:vads-u-margin-x--2 vads-u-margin-bottom--4">
          Find a VA location or in-network community care provider. For same-day
          care for minor illnesses or injuries, select Urgent care for facility
          type.
        </p>
      )}
      <CommunityCareWarningBanner shouldShow={props.showCommunityCareBanner} />
      {renderView()}
      {mapboxTokenValid && <p>&nbsp;</p>}
    </>
  );
};

const mapStateToProps = state => ({
  currentQuery: state.searchQuery,
  mobileMapPinSelected: state.searchResult.mobileMapPinSelected,
  mobileMapUpdateEnabled: facilityLocatorMobileMapUpdate(state),
  pagination: state.searchResult.pagination,
  resultTime: state.searchResult.resultTime,
  results: state.searchResult.results,
  searchError: state.searchResult.error,
  showCommunityCareBanner: showFacilityLocatorNoticeAboutNonVACare(state),
  specialties: state.searchQuery.specialties,
  suppressPPMS: facilitiesPpmsSuppressAll(state),
  usePredictiveGeolocation: facilityLocatorPredictiveLocationSearch(state),
  vaHealthServicesData: state.drupalStaticData?.vaHealthServicesData,
  vamcAutoSuggestEnabled: facilityLocatorAutosuggestVAMCServices(state),
});

const mapDispatchToProps = {
  clearSearchResults,
  fetchVAFacility,
  genBBoxFromAddress,
  genSearchAreaFromCenter,
  mapMoved,
  searchWithBounds,
  selectMobileMapPin,
  updateSearchQuery,
};

FacilitiesMap.propTypes = FacilitiesMapTypes;

export default connect(mapStateToProps, mapDispatchToProps)(FacilitiesMap);
